// Card #3308 -- scoreboard screen transitions.
//
// Two small pure pieces, kept apart from the React hook so the rules are
// unit-testable on their own:
//
//   screenKeyFor(...)   what counts as "a different screen". The board runs a
//                       full-screen transition only when this key changes.
//   pickTransition(...) which of the (theme, slideshow) transitions a given
//                       key change uses.
//
// What changes the key (the card's own rule):
//   - a mode change (the resolved mode's id: a mode-group member rotating, the
//     remote picking another mode, a default mode taking over);
//   - a single-rider screen changing rider (competitor number + rider + horse);
//   - a slideshow moving to its next slide.
// What does NOT: a value updating inside the same screen (a score arriving, a
// ranked list re-sorting, the same rider's status changing). Those re-render in
// place with no full-screen transition.
import { isSingleRiderModeType, resolveTransition } from "score-common/constants";

// Separates the screen part of a key from the slideshow slide part.
export const SLIDE_SEP = "#slide";

// The board has not loaded anything yet. A change AWAY from it is never
// transitioned: the first real screen just appears.
export const LOADING_SCREEN_KEY = "loading";

function riderPart(fields) {
  const id = fields?.entryIdentity;
  if (!id) return "";
  // The competitor number is the rider's identity on the board. Names are only
  // the fallback for a payload with no number, so a name that fills in late on
  // the SAME rider does not read as a new rider and trigger a fade.
  const cno = id.competitorNumber;
  if (cno !== undefined && cno !== null && String(cno) !== "") return `|rider:${cno}`;
  return `|rider:|${id.riderName ?? ""}|${id.horseName ?? ""}`;
}

// Pure and total: never throws on a missing or odd payload.
export function screenKeyFor({ isLoading = false, mode = null, fields = null, slideshow = null } = {}) {
  if (isLoading) return LOADING_SCREEN_KEY;
  if (!mode) return "none";
  let key = `mode:${mode.id ?? mode.modeType ?? "unknown"}`;
  if (isSingleRiderModeType(mode.modeType)) key += riderPart(fields);
  if (slideshow && Number.isInteger(slideshow.activeSlideIndex)) {
    key += `${SLIDE_SEP}${slideshow.activeSlideIndex}`;
  }
  return key;
}

function screenPart(key) {
  const at = key.indexOf(SLIDE_SEP);
  return at === -1 ? key : key.slice(0, at);
}

function isSlideKey(key) {
  return key.includes(SLIDE_SEP);
}

// The transition for a change from `prevKey` to `nextKey`. A slide-to-slide
// change inside the same slideshow screen uses the slideshow's own transition
// (which defaults to the theme's); every other change uses the theme's.
// Always a concrete { type, durationMs }.
export function pickTransition({ prevKey, nextKey, themeTransition, slideshowTransition }) {
  const slideToSlide =
    typeof prevKey === "string" &&
    typeof nextKey === "string" &&
    isSlideKey(prevKey) &&
    isSlideKey(nextKey) &&
    screenPart(prevKey) === screenPart(nextKey);
  return resolveTransition(themeTransition, slideToSlide ? slideshowTransition : undefined);
}
